import { useState } from "react";
import { Heart, Eye, Share2, ShoppingBag } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BRAND } from "@/data/mockData";

const fmt = (n) => `₹ ${n.toLocaleString("en-IN")}`;

export default function ProductCard({ product }) {
  const { has, toggle } = useWishlist();
  const active = has(product.id);
  const [open, setOpen] = useState(false);

  const share = async () => {
    const url = window.location.origin + "/shop";
    const text = `${product.name} — ${fmt(product.price)} • SIRIJA`;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast("Link copied", { description: "Share it wherever you like." });
    } catch { toast("Couldn't copy link"); }
  };

  const enquire = () => {
    const msg = encodeURIComponent(`Hi SIRIJA, I'd like to enquire about "${product.name}" (${fmt(product.price)}).`);
    window.open(`https://wa.me/${BRAND.phoneRaw}?text=${msg}`, "_blank");
  };

  return (
    <>
      <div data-testid={`product-card-${product.id}`} className="group relative">
        <div className="relative overflow-hidden rounded-2xl bg-[#F8F9FA] aspect-[4/5]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="product-image absolute inset-0 h-full w-full object-cover"
          />
          {/* top-right actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            <button
              data-testid={`wishlist-toggle-${product.id}`}
              aria-label={active ? "Remove from wishlist" : "Add to wishlist"}
              onClick={() => toggle(product)}
              className="h-9 w-9 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center shadow-sm hover:bg-white transition-colors"
            >
              <Heart className={`h-4 w-4 ${active ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#2E2E2E]"}`} strokeWidth={1.5} />
            </button>
            <button
              data-testid={`quick-view-${product.id}`}
              aria-label="Quick view"
              onClick={() => setOpen(true)}
              className="h-9 w-9 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center shadow-sm hover:bg-white transition-colors opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-[opacity,transform] duration-300"
            >
              <Eye className="h-4 w-4 text-[#2E2E2E]" strokeWidth={1.5} />
            </button>
            <button
              data-testid={`share-${product.id}`}
              aria-label="Share"
              onClick={share}
              className="h-9 w-9 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center shadow-sm hover:bg-white transition-colors opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-[opacity,transform] duration-300 delay-75"
            >
              <Share2 className="h-4 w-4 text-[#2E2E2E]" strokeWidth={1.5} />
            </button>
          </div>

          {/* bottom "Add to Cart / Enquire" bar */}
          <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
            <button
              data-testid={`enquire-${product.id}`}
              onClick={enquire}
              className="w-full h-11 rounded-full bg-[#1A1A1A] text-white text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2 hover:bg-black transition-colors"
            >
              <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.75} />
              Enquire to Buy
            </button>
          </div>
        </div>

        <div className="pt-4 px-1">
          <div className="overline text-[#A0A0A0]">{product.purity}</div>
          <div className="mt-1 flex items-baseline justify-between gap-4">
            <h3 className="font-serif text-lg text-[#1A1A1A] leading-tight tracking-tight">{product.name}</h3>
            <div className="text-sm text-[#2E2E2E] whitespace-nowrap">{fmt(product.price)}</div>
          </div>
          <p className="mt-1 text-xs text-[#2E2E2E]/60 line-clamp-1">{product.description}</p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`quick-view-dialog-${product.id}`} className="max-w-3xl p-0 overflow-hidden border-[#E5E5E5]">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="bg-[#F8F9FA] aspect-square md:aspect-auto">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <DialogHeader>
                <div className="overline text-[#A0A0A0]">{product.purity}</div>
                <DialogTitle className="font-serif text-3xl tracking-tight mt-2">{product.name}</DialogTitle>
                <DialogDescription className="sr-only">{product.description}</DialogDescription>
              </DialogHeader>
              <div className="mt-3 text-xl text-[#2E2E2E]">{fmt(product.price)}</div>
              <p className="mt-6 text-sm text-[#2E2E2E]/75 leading-relaxed">{product.description}</p>
              <ul className="mt-6 space-y-2 text-xs text-[#2E2E2E]/70">
                <li><span className="text-[#A0A0A0]">Occasion</span> · {product.occasion}</li>
                <li><span className="text-[#A0A0A0]">Gender</span> · {product.gender}</li>
                <li><span className="text-[#A0A0A0]">Collection</span> · {product.collection}</li>
              </ul>
              <div className="mt-8 flex items-center gap-3">
                <button
                  onClick={enquire}
                  data-testid={`dialog-enquire-${product.id}`}
                  className="btn-fill relative h-12 px-6 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.18em]"
                >
                  Enquire on WhatsApp
                </button>
                <button
                  onClick={() => toggle(product)}
                  data-testid={`dialog-wishlist-${product.id}`}
                  className="h-12 px-6 rounded-full border border-[#E5E5E5] text-xs uppercase tracking-[0.18em] hover:bg-[#F8F9FA] transition-colors"
                >
                  {active ? "In wishlist" : "Add to wishlist"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
